import {
  UpdateLoginFlowBody,
  UpdateRecoveryFlowBody,
  UpdateRegistrationFlowBody,
  UpdateSettingsFlowBody,
  UpdateVerificationFlowBody,
} from "@ory/kratos-client";
import { isAxiosError } from "axios";
import classNames from "classnames";
import {
  ChangeEvent,
  Dispatch,
  FormEvent,
  MouseEvent,
  SetStateAction,
} from "react";

import { Flow } from "./use-flow";
import { LocalizedLink } from "../localized-link";
import { emailValidation } from "../validation-functions/email-validation";
import {
  passwordValidation,
  passwordLengthValidation,
  passwordLowerCharValidation,
  passwordNumberValidation,
  passwordSpecialCharValidation,
  passwordUpperCharValidation,
} from "../validation-functions/password-validation";
import { GetType } from "@/types/types";

export type Values = Partial<
  | UpdateLoginFlowBody
  | UpdateRegistrationFlowBody
  | UpdateRecoveryFlowBody
  | UpdateSettingsFlowBody
  | UpdateVerificationFlowBody
>;

export interface PasswordValidation {
  complete: boolean | null;
  length: boolean | null;
  lowerChar: boolean | null;
  number: boolean | null;
  specialChar: boolean | null;
  upperChar: boolean | null;
}

export type Methods =
  | "oidcSS"
  | "password"
  | "profile"
  | "totp"
  | "webauthn"
  | "passkey"
  | "link"
  | "lookup_secret";

export const PRECONDITION_FAILED = 412;

export function getInputValue(flow: Flow, name: string): string {
  const element = flow.ui.nodes.find(
    (el) => el.attributes.node_type === "input" && el.attributes.name === name,
  );

  if (element === undefined) {
    return "";
  }

  if (element?.attributes.node_type === "input") {
    if (element.attributes.value === undefined) {
      return "";
    }

    return String(element.attributes.value);
  }

  return "";
}

export function hasInputField(flow: Flow, name: string): boolean {
  const element = flow.ui.nodes.find(
    (el) => el.attributes.node_type === "input" && el.attributes.name === name,
  );

  if (element === undefined) {
    return false;
  }

  return true;
}

export async function handleSubmit<T>(
  event: FormEvent<HTMLFormElement> | MouseEvent<HTMLButtonElement, MouseEvent>,
  onSubmit: (Values: T) => Promise<void>,
  submitMethod: "profile" | "password" | "code",
  isLoading: boolean,
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>,
  setAxiosError: Dispatch<SetStateAction<Flow | undefined>>,
) {
  // Prevent all native handlers
  event.stopPropagation();
  event.preventDefault();

  if (isLoading) {
    return Promise.resolve();
  }
  const form = event.currentTarget;

  let body: T | undefined;

  if (form && form instanceof HTMLFormElement) {
    const formData = new FormData(form);
    body = Object.fromEntries(formData) as T;
  }

  setIsLoading(true);

  try {
    await onSubmit({ ...(body as T), method: submitMethod });
  } catch (error) {
    if (isAxiosError(error)) {
      setAxiosError(error.response?.data as Flow);
    }
  } finally {
    setIsLoading(false);
  }
}

export async function handleBack<T>(
  event: MouseEvent,
  onSubmit: (Values: T) => Promise<void>,
  isLoading: boolean,
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>,
  setAxiosError: Dispatch<SetStateAction<Flow | undefined>>,
  csrfToken: string,
  email: string,
  username: string,
) {
  // Prevent all native handlers
  event.stopPropagation();
  event.preventDefault();

  if (isLoading) {
    return Promise.resolve();
  }
  const form = event.currentTarget;

  let body: T | undefined;

  if (form && form instanceof HTMLFormElement) {
    const formData = new FormData(form);
    body = Object.fromEntries(formData) as T;
  }

  setIsLoading(true);

  try {
    await onSubmit({
      ...(body as T),
      "traits.password": "",
      "traits.email": email,
      "traits.username": username,
      screen: "previous",
      csrf_token: csrfToken,
    });
    //setOryError([]);
  } catch (error) {
    if (isAxiosError(error)) {
      //errorMatcher(error);
      // TODO: Handle Axios error
      //messageMatcher(flow?.ui.messages);
      // flow?.ui.nodes.forEach((el) => {
      //   messageMatcher(el.messages);
      // });
      setAxiosError(error.response?.data as Flow);
    }
  } finally {
    setIsLoading(false);
  }
}

export async function handleNoEmail<T>(
  event: MouseEvent,
  onSubmit: (Values: T) => Promise<void>,
  isLoading: boolean,
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>,
  csrfToken: string,
  email: string,
) {
  // Prevent all native handlers
  event.stopPropagation();
  event.preventDefault();

  if (isLoading) {
    return Promise.resolve();
  }
  const form = event.currentTarget;

  let body: T | undefined;

  if (form && form instanceof HTMLFormElement) {
    const formData = new FormData(form);
    body = Object.fromEntries(formData) as T;
  }

  setIsLoading(true);

  try {
    await onSubmit({
      ...(body as T),
      code: undefined,
      email: email,
      method: "code",
      csrf_token: csrfToken,
    });
    //setOryError([]);
  } catch (error) {
    if (isAxiosError(error)) {
      //errorMatcher(error);
      // TODO: Handle Axios error
      //messageMatcher(flow?.ui.messages);
      // flow?.ui.nodes.forEach((el) => {
      //   messageMatcher(el.messages);
      // });
    }
  } finally {
    setIsLoading(false);
  }
}

export function OryMessage({
  oryFlow,
  axiosFlow,
  content,
  lang,
  profileUpdated,
}: {
  oryFlow: Flow;
  axiosFlow?: Flow | undefined;
  content: GetType<"api::auth.auth">;
  lang: string;
  profileUpdated?: boolean;
}) {
  const messages = oryFlow.ui.messages ?? [];

  if (axiosFlow) {
    axiosFlow.ui?.messages?.forEach((el) => {
      if (!messages.find((messageEl) => messageEl.id === el.id)) {
        messages.push(el);
      }
    });
  }

  if (profileUpdated) {
    if (!messages.find((el) => el.id === 0))
      messages.push({ id: 0, text: content.account_updated, type: "success" });
  }

  if (messages.length === 0) {
    return null;
  }

  return (
    <div>
      {messages?.map((el) => (
        <div key={el.id}>
          <p
            className={classNames({
              "text-danger": el.type === "error",
              "opacity-50": el.type !== "error",
            })}
          >
            {String(
              (content[`ory_error_${el.id}` as keyof typeof content] as
                string | undefined) ?? el.text,
            )}{" "}
            {el.id === 4000010 && (
              <LocalizedLink href="/account/verification" lang={lang}>
                {content.account_inactive_link_text}
              </LocalizedLink>
            )}
          </p>
        </div>
      ))}
    </div>
  );
}

export function OryInputMessage({
  flow,
  content,
  name,
}: {
  flow: Flow;
  content: GetType<"api::auth.auth">;
  name: string;
}) {
  const field = flow.ui.nodes.find(
    (el) => el.attributes.node_type === "input" && el.attributes.name === name,
  );

  if (field?.messages.length === 0) return null;

  return (
    <div className="opacity-50">
      {field?.messages.map((el) => (
        <p key={el.id}>
          {String(
            (content[`ory_error_${el.id}` as keyof typeof content] as
              string | undefined) ?? el.text,
          )}
        </p>
      ))}
    </div>
  );
}

export function PasswordEye({
  passwordVisibility,
  setPasswordVisibility,
  warning = true,
}: {
  passwordVisibility: boolean;
  setPasswordVisibility: Dispatch<SetStateAction<boolean>>;
  warning?: boolean;
}) {
  return (
    <>
      {passwordVisibility && (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          className={classNames("bi bi-eye-fill", {
            "icon-indent": warning,
            "icon-indent-no-warning": !warning,
          })}
          viewBox="0 0 16 16"
          onClick={() => setPasswordVisibility(!passwordVisibility)}
        >
          <path d="M10.5 8a2.5 2.5 0 1 1-5 0 2.5 2.5 0 0 1 5 0" />
          <path d="M0 8s3-5.5 8-5.5S16 8 16 8s-3 5.5-8 5.5S0 8 0 8m8 3.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7" />
        </svg>
      )}

      {!passwordVisibility && (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          fill="currentColor"
          className={classNames("bi bi-eye-slash-fill", {
            "icon-indent": warning,
            "icon-indent-no-warning": !warning,
          })}
          viewBox="0 0 16 16"
          onClick={() => setPasswordVisibility(!passwordVisibility)}
        >
          <path d="m10.79 12.912-1.614-1.615a3.5 3.5 0 0 1-4.474-4.474l-2.06-2.06C.938 6.278 0 8 0 8s3 5.5 8 5.5a7 7 0 0 0 2.79-.588M5.21 3.088A7 7 0 0 1 8 2.5c5 0 8 5.5 8 5.5s-.939 1.721-2.641 3.238l-2.062-2.062a3.5 3.5 0 0 0-4.474-4.474z" />
          <path d="M5.525 7.646a2.5 2.5 0 0 0 2.829 2.829zm4.95.708-2.829-2.83a2.5 2.5 0 0 1 2.829 2.829zm3.171 6-12-12 .708-.708 12 12z" />
        </svg>
      )}
    </>
  );
}

export function onPasswordChange(
  e: ChangeEvent<HTMLInputElement>,
  passwordTimer: NodeJS.Timeout | undefined,
  setValidPassword: Dispatch<SetStateAction<PasswordValidation>>,
  setPasswordTimer: Dispatch<SetStateAction<NodeJS.Timeout | undefined>>,
) {
  if (passwordTimer !== undefined) {
    clearTimeout(passwordTimer);
  }

  setPasswordTimer(
    setTimeout(() => {
      const input = e.target.value;

      // Password Empty
      if (input.length === 0) {
        setValidPassword({
          complete: null,
          length: null,
          lowerChar: null,
          number: null,
          specialChar: null,
          upperChar: null,
        });
        return;
      }

      // Overall Password Validation
      setValidPassword({
        complete: passwordValidation(input),
        length: passwordLengthValidation(input),
        lowerChar: passwordLowerCharValidation(input),
        number: passwordNumberValidation(input),
        specialChar: passwordSpecialCharValidation(input),
        upperChar: passwordUpperCharValidation(input),
      });
    }, 800),
  );
}

export function onIdentifierChange(
  e: string,
  identifierTimer: NodeJS.Timeout | undefined = undefined,
  setEmail: Dispatch<SetStateAction<string>>,
  setValidIdentifier: Dispatch<SetStateAction<boolean | null>>,
  setIdentifierTimer: Dispatch<SetStateAction<NodeJS.Timeout | undefined>>,
) {
  setEmail(e.trim());

  if (identifierTimer !== undefined) {
    clearTimeout(identifierTimer);
  }

  setIdentifierTimer(
    setTimeout(() => {
      const input = e.trim();

      if (input.length >= 5 && emailValidation(input)) {
        setValidIdentifier(true);
        return;
      }

      if (input.length === 0) {
        setValidIdentifier(null);
        return;
      }

      setValidIdentifier(false);
    }, 800),
  );
}
